import { useQuery, useMutation, keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { turfApi } from '../lib/api'
import type {
  CreateTurfRequest,
  UpdateTurfRequest,
  CutTurfRequest,
  ExportTurfToListRequest,
  TurfFilters,
  CandidateSearchRequest,
  AddOpportunisticStopRequest,
} from '../types/turf'

export function useTurfs(filters?: TurfFilters) {
  return useQuery({
    queryKey: ['turfs', filters],
    queryFn: async () => {
      const response = await turfApi.getAll(filters)
      return response.data
    },
  })
}

export function useTurfDetail(id: string | undefined, scheduleAt?: string) {
  return useQuery({
    queryKey: ['turfs', id, scheduleAt ?? null],
    queryFn: async () => {
      if (!id) throw new Error('Turf ID is required')
      const response = await turfApi.getById(id, scheduleAt)
      return response.data
    },
    enabled: !!id,
    // Changing scheduleAt changes the key; keep the previous turf on screen meanwhile.
    placeholderData: keepPreviousData,
  })
}

export function useCreateTurf() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateTurfRequest) =>
      turfApi.create(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turfs'] })
    },
  })
}

export function useUpdateTurf(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateTurfRequest) =>
      turfApi.update(id, data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['turfs', id], data)
      queryClient.invalidateQueries({ queryKey: ['turfs'] })
    },
  })
}

export function useDeleteTurf() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => turfApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turfs'] })
    },
  })
}

export function useCutTurf() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CutTurfRequest) =>
      turfApi.cut(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turfs'] })
    },
  })
}

export function useOptimizeWalkOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      turfApi.optimizeOrder(id).then(r => r.data),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['turfs', id] })
    },
  })
}

export function useExportTurfToList() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ExportTurfToListRequest }) =>
      turfApi.exportToList(id, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turfs'] })
      queryClient.invalidateQueries({ queryKey: ['lists'] })
    },
  })
}

function useTurfEdit<TVars>(fn: (vars: TVars) => Promise<unknown>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turfs'] })
      queryClient.invalidateQueries({ queryKey: ['turf-maps'] })
    },
  })
}

export const useSetPlacements = (turfId: string | undefined) =>
  useTurfEdit((items: { turfMemberId: string; locationSource: 'Home' | 'Work' }[]) => turfApi.setPlacements(turfId!, items))
export const useMoveTurfMember = (turfId: string | undefined) =>
  useTurfEdit(({ memberId, toPosition }: { memberId: string; toPosition: number }) => turfApi.moveMember(turfId!, memberId, toPosition))
export const useRemoveTurfMember = (turfId: string | undefined) =>
  useTurfEdit((memberId: string) => turfApi.removeMember(turfId!, memberId))

// [O4] The corridor search is a read, but a parameterised one the page holds in state: a mutation.
export function useFindCandidates(turfId: string | undefined) {
  return useMutation({
    mutationFn: (body: CandidateSearchRequest) =>
      turfApi.findCandidates(turfId!, body).then(r => r.data),
  })
}
export const useAddOpportunisticStop = (turfId: string | undefined) =>
  useTurfEdit((body: AddOpportunisticStopRequest) => turfApi.addOpportunisticStop(turfId!, body).then(r => r.data))
export const useUpdateStopScript = (turfId: string | undefined) =>
  useTurfEdit(({ stopId, scriptId }: { stopId: string; scriptId: string | null }) => turfApi.updateStopScript(turfId!, stopId, scriptId))
export const useRemoveOpportunisticStop = (turfId: string | undefined) =>
  useTurfEdit((stopId: string) => turfApi.removeOpportunisticStop(turfId!, stopId))
